// Draft calculation utilities (projections, replacement value, recommendations)

export const calculateTeamProjected = (teamIndex, draftedPlayers, playersByPosition) => {
  let total = 0;
  Object.entries(draftedPlayers).forEach(([fieldId, playerName]) => {
    if (fieldId.startsWith(`${teamIndex}-`)) {
      // Find player in playersByPosition
      for (const pos in playersByPosition) {
        const player = playersByPosition[pos].find(p => p.name === playerName);
        if (player) {
          total += player.projected;
          break;
        }
      }
    }
  });
  return total.toFixed(1);
};

// Look up one player in a final points file (from get_final_player_point.py).
// Mirrors calculateTeamProjected: the player is scored from the first position
// bucket that lists them, so TE-eligible players counted in the WR list project
// and score from the same bucket. Matches by player_id when both sides have
// one, otherwise by name. Returns null when the player is not in the file.
export const findPlayerFinalPoints = (playerName, playersByPosition, finalPoints) => {
  for (const pos in playersByPosition) {
    const player = playersByPosition[pos].find(p => p.name === playerName);
    if (!player) continue;
    const records = finalPoints[pos] || [];
    const record =
      (player.player_id && records.find(r => r.player_id === player.player_id)) ||
      records.find(r => r.name === playerName);
    return record && typeof record.points === 'number' ? record.points : null;
  }
  return null;
};

// Team total from the final points file, all slots including bench, mirroring
// calculateTeamProjected so the two numbers shown side by side are comparable.
export const calculateTeamFinal = (teamIndex, draftedPlayers, playersByPosition, finalPoints) => {
  let total = 0;
  Object.entries(draftedPlayers).forEach(([fieldId, playerName]) => {
    if (fieldId.startsWith(`${teamIndex}-`)) {
      total += findPlayerFinalPoints(playerName, playersByPosition, finalPoints) || 0;
    }
  });
  return total.toFixed(1);
};

// Score every roster against a final points file. The official score counts
// starters only; bench points are reported separately.
export const calculateFinalTeamScores = (config, draftedPlayers, playersByPosition, finalPoints) => {
  const findFinalPoints = (playerName) =>
    findPlayerFinalPoints(playerName, playersByPosition, finalPoints);

  const scores = [];
  for (let teamIndex = 0; teamIndex < config.teamCount; teamIndex++) {
    let official = 0;
    let bench = 0;
    const unmatched = [];
    Object.entries(config.positions).forEach(([position, count]) => {
      for (let i = 0; i < count; i++) {
        const playerName = draftedPlayers[`${teamIndex}-${position}-${i}`];
        if (!playerName) continue;
        const points = findFinalPoints(playerName);
        if (points === null) {
          unmatched.push(playerName);
          continue;
        }
        if (position === 'BENCH') {
          bench += points;
        } else {
          official += points;
        }
      }
    });
    scores.push({
      teamIndex,
      teamName: config.teamNames[teamIndex] || `Team ${teamIndex + 1}`,
      official: Math.round(official * 100) / 100,
      withBench: Math.round((official + bench) * 100) / 100,
      unmatched
    });
  }
  return scores.sort((a, b) => b.official - a.official);
};

const REAL_POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];

// Expected fraction of the season a starter at each position is unavailable
// (injury/usage), ordered RB > WR > TE > QB > K > DEF. The season model uses
// it as the share of a season a bench cover (or waiver replacement) inherits
// from its starter, and it can be overridden per-call via options.injuryProb.
// It does NOT discount the starter: projections already price in expected
// games missed, so docking starters here would double-count availability.
// Per-player accuracy would come from an `injuryRisk` field on each player; this
// position-average is the data-free default.
export const POSITION_INJURY_PROB = {
  RB: 0.30, WR: 0.22, TE: 0.18, QB: 0.12, K: 0.03, DEF: 0.0
};

// How strongly recommendations penalize projection uncertainty (a player's
// optional `sd` field, emitted by bin/miff/get_player_projections.py). Positive =
// prefer the reliable player when projections are similar. The bench value is
// negative: late/bench picks should chase upside instead, since a bust there
// costs little while a boom wins weeks. Players without sd are unaffected.
export const RISK_LAMBDA_STARTERS = 0.35;
export const RISK_LAMBDA_BENCH = -0.15;

// One season-outcome draw for a player: Normal(projected, sd) truncated at 0,
// via Box-Muller. Without an sd the projection is treated as certain.
const sampleProjection = (player, rng) => {
  const sd = player.sd || 0;
  if (!sd) return player.projected;
  const z = Math.sqrt(-2 * Math.log(Math.max(rng(), 1e-12))) * Math.cos(2 * Math.PI * rng());
  return Math.max(0, player.projected + sd * z);
};

// Real positions that can fill a given roster slot type.
export const eligibleRealPositions = (slotType) => {
  switch (slotType) {
    case 'FLEX': return ['RB', 'WR', 'TE'];
    case 'SFLEX': return ['QB', 'RB', 'WR', 'TE'];
    case 'BENCH': return REAL_POSITIONS;
    default: return [slotType];
  }
};

// Human drafters leave kickers and defenses for the last rounds regardless of
// projected points, so simulated opponents never take one while any of their
// skill starter slots is open. Our own simulated picks stay unconstrained:
// with realistic opponents the rollouts show the top K/DEF survive, so
// waiting on them wins on its own merits instead of by decree.
const LATE_ROUND_POSITIONS = ['K', 'DEF'];
const SKILL_STARTER_SLOTS = ['QB', 'RB', 'WR', 'TE', 'FLEX', 'SFLEX'];
const withoutLateRoundPositions = (usable, open) => {
  if (SKILL_STARTER_SLOTS.some(type => open[type])) {
    LATE_ROUND_POSITIONS.forEach(p => usable.delete(p));
  }
  return usable;
};

const teamForPick = (globalPickIndex, teamCount) => {
  const round = Math.floor(globalPickIndex / teamCount);
  const pickInRound = globalPickIndex % teamCount;
  return round % 2 === 0 ? pickInRound : teamCount - 1 - pickInRound;
};

// Open (unfilled) slots for one team, keyed by slot type. Slot types with no
// remaining openings are omitted, matching how the rest of the app reasons
// about "what this team still needs".
export const openSlotsForTeam = (teamIndex, config, draftedPlayers) => {
  const open = {};
  Object.entries(config.positions).forEach(([type, perTeam]) => {
    if (perTeam === 0) return;
    let filled = 0;
    for (let i = 0; i < perTeam; i++) {
      if (draftedPlayers[`${teamIndex}-${type}-${i}`]) filled++;
    }
    if (filled < perTeam) open[type] = perTeam - filled;
  });
  return open;
};

// Open (unfilled) STARTING slots league-wide, keyed by slot type; bench excluded.
const openStarterSlots = (config, draftedPlayers) => {
  const openByType = {};
  Object.entries(config.positions).forEach(([type, perTeam]) => {
    if (perTeam === 0 || type === 'BENCH') return;
    let open = 0;
    for (let t = 0; t < config.teamCount; t++) {
      let filled = 0;
      for (let i = 0; i < perTeam; i++) {
        if (draftedPlayers[`${t}-${type}-${i}`]) filled++;
      }
      open += perTeam - filled;
    }
    openByType[type] = open;
  });
  return openByType;
};

// Replacement level per real position: the projected points of the player you
// can still expect to get freely later. We walk the *remaining* pool (each
// position sorted by projection, `isTaken` skipping players gone since) and
// read off the player at the index equal to the number of open STARTING slots
// the position must fill league-wide. FLEX/SFLEX demand is split across
// eligible positions in proportion to their base demand.
const replacementForDemand = (openByType, sortedAvailable, isTaken = () => false) => {
  const demand = {};
  REAL_POSITIONS.forEach(p => { demand[p] = openByType[p] || 0; });

  const distributeFlex = (slotType) => {
    const open = openByType[slotType] || 0;
    if (open === 0) return;
    const eligible = eligibleRealPositions(slotType);
    const weightSum = eligible.reduce((sum, p) => sum + demand[p], 0);
    eligible.forEach(p => {
      const share = weightSum > 0 ? demand[p] / weightSum : 1 / eligible.length;
      demand[p] += open * share;
    });
  };
  distributeFlex('FLEX');
  distributeFlex('SFLEX');

  const replacement = {};
  REAL_POSITIONS.forEach(p => {
    let remaining = Math.floor(demand[p]);
    let level = null;
    for (const player of sortedAvailable[p] || []) {
      if (isTaken(player)) continue;
      level = player;
      if (remaining === 0) break;
      remaining--;
    }
    replacement[p] = level ? level.projected : 0;
  });
  return replacement;
};

const sortByProjection = (availablePlayers) => {
  const sorted = {};
  REAL_POSITIONS.forEach(p => {
    sorted[p] = [...(availablePlayers[p] || [])].sort((a, b) => b.projected - a.projected);
  });
  return sorted;
};

export const computeReplacementLevels = (config, availablePlayers, draftedPlayers) =>
  replacementForDemand(openStarterSlots(config, draftedPlayers), sortByProjection(availablePlayers));

const vorp = (player, position, replacement) => player.projected - (replacement[position] || 0);

// Opponents draft by market consensus, not by our projections. Each pick is
// drawn from the first few still-available players in ADP order that the team
// can roster; weight k is the chance the pick is the k-th of those. The tail
// is what lets a rollout take the second-best player at a position, so "who
// survives until our next pick" is genuinely uncertain across simulations.
export const OPPONENT_PICK_WEIGHTS = [0.5, 0.25, 0.13, 0.08, 0.04];

// Every available player in the order opponents are expected to take them:
// by ADP, then players the market never drafts (no adp) by value over
// replacement. Built once per recommendation and shared by every rollout.
export const opponentDraftOrder = (availablePlayers, replacement) => {
  const order = [];
  REAL_POSITIONS.forEach(position => {
    (availablePlayers[position] || []).forEach(player => {
      order.push({ player, position, value: vorp(player, position, replacement) });
    });
  });
  return order.sort((a, b) =>
    (a.player.adp ?? Infinity) - (b.player.adp ?? Infinity) || b.value - a.value
  );
};

// One opponent pick for a team with `open` slots. Returns { player, position }
// or null when nothing usable remains. Mutates nothing.
export const chooseOpponentPick = (order, open, taken, rng, weights = OPPONENT_PICK_WEIGHTS) => {
  const usable = new Set();
  Object.keys(open).forEach(type => eligibleRealPositions(type).forEach(p => usable.add(p)));
  withoutLateRoundPositions(usable, open);

  const choices = [];
  for (const choice of order) {
    if (choices.length === weights.length) break;
    if (!taken.has(choice.player.name) && usable.has(choice.position)) choices.push(choice);
  }
  if (choices.length === 0) return null;

  let threshold = rng() * weights.slice(0, choices.length).reduce((a, b) => a + b, 0);
  for (let i = 0; i < choices.length; i++) {
    threshold -= weights[i];
    if (threshold <= 0) return choices[i];
  }
  return choices[0];
};

// Expected season points for a roster. Starter projections already include
// expected games missed, so each starter counts in full; the injury rate only
// sets how much of a season the best eligible bench player (else an undrafted
// replacement) inherits while the starter is out. Bench players therefore earn
// value only through the cover they provide, which is higher at injury-prone
// positions. Riskiest slots are covered first. `pointsOf` lets Monte Carlo
// callers value the roster at a sampled season outcome instead of the mean.
export const expectedRosterValue = (picks, replacement, injuryProb, pointsOf = (p) => p.projected) => {
  const benchByPosition = {};
  picks.forEach(({ player, position, slotType }) => {
    if (slotType === 'BENCH') {
      (benchByPosition[position] = benchByPosition[position] || []).push(player);
    }
  });
  Object.values(benchByPosition).forEach(list => list.sort((a, b) => pointsOf(b) - pointsOf(a)));

  const starters = picks
    .filter(p => p.slotType && p.slotType !== 'BENCH')
    .sort((a, b) => (injuryProb[b.position] || 0) - (injuryProb[a.position] || 0));

  let total = 0;
  starters.forEach(({ player, position, slotType }) => {
    const p = injuryProb[position] || 0;
    let coverPosition = null;
    let coverProjected = replacement[position] || 0;
    eligibleRealPositions(slotType).forEach(pos => {
      const list = benchByPosition[pos];
      if (list && list.length && pointsOf(list[0]) > coverProjected) {
        coverProjected = pointsOf(list[0]);
        coverPosition = pos;
      }
    });
    if (coverPosition) benchByPosition[coverPosition].shift();
    total += pointsOf(player) + p * coverProjected;
  });
  return total;
};

// Play out the entire remaining draft once. Our team picks greedily by VORP
// against the replacement levels implied by the slots still open at that
// point (after an optional forced opening move); opponents follow ADP with
// variance. Everyone drafts on mean projections (nobody is clairvoyant about
// outcomes), but the final roster is valued at one sampled season outcome per
// player, so candidates carrying uncertain players produce a wider spread of
// totals.
const simulateFullDraft = (
  config, draftedPlayers, sortedAvailable, opponentOrder, openStarters,
  ourTeam, forcedFirstPick, rng, injuryProb, opponentWeights
) => {
  const sampledPoints = new Map();
  const pointsOf = (player) => {
    if (!sampledPoints.has(player.name)) sampledPoints.set(player.name, sampleProjection(player, rng));
    return sampledPoints.get(player.name);
  };

  const totalSlots = Object.entries(config.positions)
    .reduce((sum, [, perTeam]) => sum + perTeam * config.teamCount, 0);
  const startIndex = Object.values(draftedPlayers).length;

  const taken = new Set();
  const isTaken = (player) => taken.has(player.name);
  const openByType = { ...openStarters };
  const teamOpen = {};
  const getOpen = (team) => {
    if (!teamOpen[team]) teamOpen[team] = openSlotsForTeam(team, config, draftedPlayers);
    return teamOpen[team];
  };

  const ourBestPick = (open) => {
    const replacement = replacementForDemand(openByType, sortedAvailable, isTaken);
    let best = null;
    Object.keys(open).forEach(type => eligibleRealPositions(type).forEach(position => {
      const player = sortedAvailable[position].find(p => !isTaken(p));
      if (!player) return;
      const value = vorp(player, position, replacement);
      if (!best || value > best.value) best = { player, position, value };
    }));
    return best;
  };

  const ourPicks = [];
  for (let index = startIndex; index < totalSlots; index++) {
    const team = teamForPick(index, config.teamCount);
    const open = getOpen(team);

    let chosen;
    if (index === startIndex && forcedFirstPick) {
      chosen = forcedFirstPick;
    } else if (team === ourTeam) {
      chosen = ourBestPick(open);
    } else {
      chosen = chooseOpponentPick(opponentOrder, open, taken, rng, opponentWeights);
    }
    if (!chosen) continue;

    taken.add(chosen.player.name);
    let usedSlotType = null;
    for (const type of [chosen.position, 'FLEX', 'SFLEX', 'BENCH']) {
      if (open[type] && eligibleRealPositions(type).includes(chosen.position)) {
        open[type] -= 1;
        if (open[type] === 0) delete open[type];
        usedSlotType = type;
        break;
      }
    }
    if (usedSlotType && usedSlotType !== 'BENCH') openByType[usedSlotType] -= 1;
    if (team === ourTeam) {
      ourPicks.push({ player: chosen.player, position: chosen.position, slotType: usedSlotType });
    }
  }
  // With the draft over, replacement level is the best player left undrafted:
  // the waiver pickup an injured starter's slot would actually fall back to.
  const waiverLevel = replacementForDemand(openByType, sortedAvailable, isTaken);
  return expectedRosterValue(ourPicks, waiverLevel, injuryProb, pointsOf);
};

// A second same-position candidate is also evaluated as an opening move when
// its projection is within this fraction of the top player's, so near-ties get
// decided by the risk-adjusted simulation score rather than raw ranking.
const CANDIDATE_TIE_FRACTION = 0.9;

// Small seeded PRNG (mulberry32): a deterministic Math.random replacement so
// the Monte Carlo recommender returns the same pick for the same board instead
// of flickering between near-tied candidates on every recompute.
export const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Deterministic 32-bit hash (FNV-1a) of the current draft state, used as the
// simulation seed. Keys are sorted so the seed depends only on which players
// sit in which slots, not on object insertion order.
export const draftStateSeed = (draftedPlayers) => {
  const key = Object.entries(draftedPlayers)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([field, name]) => `${field}=${name}`)
    .join('|');
  let hash = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

// An rng seeded from the board state: identical rosters produce identical
// simulation streams (so identical recommendations), while any pick changes
// the seed and reshuffles the draws.
export const createDraftRng = (draftedPlayers) => mulberry32(draftStateSeed(draftedPlayers));

// Tier 2: directly maximize the team's expected total roster points. For each
// reasonable opening move, simulate the rest of the draft many times; each run
// values the final roster at sampled player outcomes (see simulateFullDraft),
// and the move is scored by mean - riskLambda * sd of those totals. Similar
// means therefore resolve toward the more reliable player while starter slots
// remain to fill, and toward upside once only bench slots are left.
// `ev` here is the EXPECTED FINAL ROSTER TOTAL for that opening move (not VORP).
export const getRecommendedPlayerMonteCarlo = (
  draftedPlayers, config, availablePlayers, options = {}
) => {
  const simulations = options.simulations || 80;
  const injuryProb = options.injuryProb || POSITION_INJURY_PROB;

  // Common random numbers: with a seed, run s starts from an identical stream
  // for every candidate, so candidates are compared across the same simulated
  // futures and their scores differ by the opening move rather than by luck of
  // the draw. Sharing one stream across candidates instead leaves a sampling
  // error of the same order as the gap between near-tied players, which is
  // enough to flip which one is recommended. Without a seed every run shares
  // the caller's rng.
  const sharedRng = options.rng || Math.random;
  const rngForRun = (run) => (options.seed == null ? sharedRng : mulberry32(options.seed + run));

  const totalPicks = Object.values(draftedPlayers).length;
  const roundNumber = Math.floor(totalPicks / config.teamCount);
  const pickInRound = totalPicks % config.teamCount;
  const draftingTeam = roundNumber % 2 === 0
    ? pickInRound
    : config.teamCount - 1 - pickInRound;

  let nextPickIndex = totalPicks + 1;
  while (teamForPick(nextPickIndex, config.teamCount) !== draftingTeam) nextPickIndex++;
  const picksUntilNext = nextPickIndex - totalPicks - 1;

  const openStarters = openStarterSlots(config, draftedPlayers);
  const sortedAvailable = sortByProjection(availablePlayers);
  const replacement = replacementForDemand(openStarters, sortedAvailable);
  const opponentOrder = opponentDraftOrder(availablePlayers, replacement);
  const opponentWeights = options.opponentPickWeights || OPPONENT_PICK_WEIGHTS;

  const remainingSpots = openSlotsForTeam(draftingTeam, config, draftedPlayers);
  const usablePositions = new Set();
  Object.keys(remainingSpots).forEach(type =>
    eligibleRealPositions(type).forEach(p => usablePositions.add(p))
  );

  // K and DEF are only offered as opening moves in the last 2 * (K + DEF slots)
  // rounds of this team's draft; before that the rollouts decide among skill
  // positions only. (A stricter "defer until forced" rule lost the round-12
  // kicker run and cost ~100 points per draft in bin/miff/backtest_draft_policy.mjs.)
  const picksLeft = Object.values(remainingSpots).reduce((sum, count) => sum + count, 0);
  const lateRoundWindow = 2 * LATE_ROUND_POSITIONS.reduce((sum, p) => sum + (config.positions[p] || 0), 0);
  if (picksLeft > lateRoundWindow) {
    LATE_ROUND_POSITIONS.forEach(p => usablePositions.delete(p));
  }

  const startersOpen = Object.entries(remainingSpots)
    .filter(([type]) => type !== 'BENCH')
    .reduce((sum, [, count]) => sum + count, 0);
  const riskLambda = options.riskLambda
    ?? (startersOpen > 0 ? RISK_LAMBDA_STARTERS : RISK_LAMBDA_BENCH);

  const candidates = [];
  usablePositions.forEach(position => {
    const [top, runnerUp] = sortedAvailable[position];
    if (!top) return;
    candidates.push({ player: top, position });
    if (runnerUp && runnerUp.projected >= top.projected * CANDIDATE_TIE_FRACTION) {
      candidates.push({ player: runnerUp, position });
    }
  });

  if (candidates.length === 0) {
    return {
      player: null, position: null, ev: -Infinity,
      picksUntilNext, futureProjected: 0, positionEVs: {}, dropoff: -Infinity
    };
  }

  const positionEVs = {};
  let best = null;
  candidates.forEach(candidate => {
    const totals = [];
    for (let s = 0; s < simulations; s++) {
      totals.push(simulateFullDraft(
        config, draftedPlayers, sortedAvailable, opponentOrder, openStarters,
        draftingTeam, candidate, rngForRun(s), injuryProb, opponentWeights
      ));
    }
    const expectedTotal = totals.reduce((sum, t) => sum + t, 0) / simulations;
    const variance = totals.reduce((sum, t) => sum + (t - expectedTotal) ** 2, 0) / simulations;
    candidate.expectedTotal = expectedTotal;
    candidate.score = expectedTotal - riskLambda * Math.sqrt(variance);
    const existing = positionEVs[candidate.position];
    if (!existing || candidate.score > existing.score) {
      positionEVs[candidate.position] = {
        ev: expectedTotal,
        score: candidate.score,
        future: 0,
        picks: remainingSpots[candidate.position] || 0,
        dropoff: 0
      };
    }
    if (!best || candidate.score > best.score) best = candidate;
  });

  // Express drop-off as how much risk-adjusted score we'd lose by choosing
  // instead the second-best opening move.
  const secondBest = candidates
    .filter(c => c !== best)
    .reduce((max, c) => (c.score > (max?.score ?? -Infinity) ? c : max), null);
  const dropoff = secondBest ? best.score - secondBest.score : best.expectedTotal;

  return {
    player: best.player,
    position: best.position,
    ev: best.expectedTotal,
    picksUntilNext,
    futureProjected: 0,
    positionEVs,
    dropoff
  };
};
